import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import {
  useMergedShopAddon,
  useGetFormulaBenefits,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { ProductDetailSkeleton } from '@screens/ShopPage/components/ProductDetailSkeleton';
import { Button } from '@/components/design-system/Button';
import { getAddOnFormulaImageUrl } from '@utils/nutrition/getFormulaImageUrl';
import { AddonIngredientsSheet } from '@screens/ShopPage/components/AddonIngredientsSheet';
import { AddonBenefitsSheet } from '@screens/ShopPage/components/AddonBenefitsSheet';
import { AddonHowToUseIt } from '@screens/ShopPage/components/AddonHowToUseIt';
import { AddonHowItWorks } from '@screens/ShopPage/components/AddonHowItWorks';
import { AddonWhatsInside } from '@screens/ShopPage/components/AddonWhatsInside';
import { AddonFAQ } from '@screens/ShopPage/components/AddonFAQ';
import { getATSSections } from '@screens/HomePage/TriSynbiotic/useATSData';

export const AddonDetailPage = () => {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMergedShopAddon();

  const addOn = data?.addon?.find((a) => a.productId === productId);

  const { data: formulaBenefitsData } = useGetFormulaBenefits(
    Number(addOn?.product_id),
  );

  const shouldShowBenefitsButton =
    (formulaBenefitsData?.goals?.length ?? 0) > 0 ||
    (formulaBenefitsData?.scores?.length ?? 0) > 0 ||
    (formulaBenefitsData?.connections?.length ?? 0) > 0;

  if (isLoading) {
    return <ProductDetailSkeleton />;
  }

  if (isError || !addOn) {
    return <ErrorLoadingPage errorType="error" hideNavBar />;
  }

  const titleParts = addOn.title?.split('™');
  const mainTitle = titleParts?.[0];
  const trademark = titleParts && titleParts.length > 1 ? '™' : '';
  const sections = addOn.addOnDetail
    ? getATSSections(addOn.addOnDetail)
    : null;
  const disclaimerText = sections?.disclaimer?.text ?? addOn.disclaimer;

  return (
    <section className="transition-colors min-h-screen bg-gradient-to-b from-[#F0F7F1] to-[#F3F3F0]">
      <div className="sticky top-0 z-50">
        <div className="page-container flex items-center py-3">
          <button
            onClick={() => navigate('/shop?tab=addon')}
            className="text-gray-600 hover:text-gray-800"
          >
            <ChevronLeft className="h-6 w-6" />
            <span className="sr-only">Back</span>
          </button>
        </div>
      </div>

      <div className="page-container pb-20">
        <div className="mx-auto max-w-2xl p-4 md:p-6 lg:p-8">
          <div className="text-center mb-10">
            <img
              src={getAddOnFormulaImageUrl(addOn.image_url)}
              alt={addOn.title}
              className="h-36 mx-auto mb-6 w-36 object-contain"
            />
            <h1 className="typography-display2 text-primary mb-6">
              {mainTitle}
              {trademark && <sup className="text-primary">{trademark}</sup>}
            </h1>

            <div className="flex justify-center space-x-3 mb-8">
              <AddonIngredientsSheet
                productId={addOn.product_id}
                ingredientCount={addOn.ingredients_count}
                productName={addOn.title}
                productImageSrc={getAddOnFormulaImageUrl(addOn.image_url)}
              />
              {shouldShowBenefitsButton && (
                <AddonBenefitsSheet
                  formulaBenefitsData={formulaBenefitsData}
                  productName={addOn.title}
                  productImageSrc={getAddOnFormulaImageUrl(addOn.image_url)}
                />
              )}
            </div>

          </div>
          {sections && (
            <div className="font-sans text-left">
              {sections.howToUse && sections.howToUse.items?.length ? (
                <>
                  <Spacer />
                  <AddonHowToUseIt
                    section={sections.howToUse}
                    notesClassName="bg-white rounded-[6px]"
                  />
                </>
              ) : null}

              {sections.howItWorks && sections.howItWorks.items?.length ? (
                <>
                  <Spacer />
                  <AddonHowItWorks section={sections.howItWorks} />
                </>
              ) : null}

              {sections.whatsInside?.items?.length || sections.delivered ? (
                <>
                  <Spacer />
                  <AddonWhatsInside
                    whatsInside={sections.whatsInside}
                    delivered={sections.delivered}
                  />
                </>
              ) : null}

              {sections.faq && sections.faq.faqs?.length ? (
                <>
                  <Spacer />
                  <AddonFAQ section={sections.faq} />
                </>
              ) : null}
            </div>
          )}

          {disclaimerText && (
            <div className="border-2 border-[#888] p-3.5 mt-8">
              <p className="font-sans font-light text-sm leading-[130%] text-tertiary">
                {disclaimerText}
              </p>
            </div>
          )}

          <div className="sticky bottom-0 py-6 mt-10 bg-gradient-to-t from-[#F4F2ED] to-transparent">
            <Button
              className="w-full h-14 rounded-full bg-black text-white hover:bg-black/80 max-w-xl mx-auto flex"
              onClick={() => {
                navigate('/shop?tab=addon');
              }}
              data-testid="addonDetail-orderButton"
            >
              Order Formula
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const Spacer = () => <div className="my-8" />;
